/**
 * Component: sa-logo-carousel
 * Description: Logo carousel component with marquee effect - smooth scrolling and pause on hover
 */
if (!customElements.get('sa-logo-carousel')) {
  class SaLogoCarousel extends HTMLElement {
    constructor() {
      super();
      this.marqueeWrapper = null;
      this.marqueeContent = null;
      this.animation = null;
      this.isPaused = false;
      this.handleMouseEnter = this.handleMouseEnter.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
      this.initMarquee = this.initMarquee.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', this.initMarquee);
      } else {
        setTimeout(this.initMarquee, 100);
      }
      window.addEventListener('resize', this.handleResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
      const wrapper = this.querySelector('.sa-logo-carousel__wrapper');
      if (wrapper) {
        wrapper.removeEventListener('mouseenter', this.handleMouseEnter);
        wrapper.removeEventListener('mouseleave', this.handleMouseLeave);
      }
      this.stopAnimation();
    }

    handleResize() {
      // Debounce resize
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.initMarquee();
      }, 250);
    }

    initMarquee() {
      this.marqueeWrapper = this.querySelector('.sa-logo-carousel__wrapper');
      this.marqueeContent = this.querySelector('.sa-logo-carousel__content');
      
      if (!this.marqueeWrapper || !this.marqueeContent) {
        return;
      }

      // Remove existing event listeners
      this.marqueeWrapper.removeEventListener('mouseenter', this.handleMouseEnter);
      this.marqueeWrapper.removeEventListener('mouseleave', this.handleMouseLeave);

      // Add event listeners for pause on hover
      this.marqueeWrapper.addEventListener('mouseenter', this.handleMouseEnter);
      this.marqueeWrapper.addEventListener('mouseleave', this.handleMouseLeave);

      // Clone content for seamless loop
      this.cloneContent();

      // Wait for next frame to ensure clones are rendered, then calculate animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.updateAnimation();
        });
      });
    }

    cloneContent() {
      // Remove existing clones
      const existingClones = this.marqueeContent.querySelectorAll('.marquee-clone');
      existingClones.forEach(clone => clone.remove());

      // Clone all slides
      const slides = this.marqueeContent.querySelectorAll('.sa-logo-carousel__slide');
      slides.forEach(slide => {
        const clone = slide.cloneNode(true);
        clone.classList.add('marquee-clone');
        this.marqueeContent.appendChild(clone);
      });
    }

    updateAnimation() {
      // Get autoplay speed based on screen size
      const isMobile = window.innerWidth <= 767;
      const autoplaySpeedAttr = isMobile 
        ? this.getAttribute('data-autoplay-speed-mobile') 
        : this.getAttribute('data-autoplay-speed');
      const autoplaySpeed = parseInt(autoplaySpeedAttr) * 3;
      
      // Calculate content width
      const contentWidth = this.marqueeContent.scrollWidth / 2; // Divide by 2 because we cloned
      const wrapperWidth = this.marqueeWrapper.offsetWidth;
      
      // Calculate duration based on speed and distance
      // autoplaySpeed is the time to move one wrapper width
      // We need to move contentWidth distance
      const duration = (autoplaySpeed * contentWidth) / wrapperWidth;
      
      // Stop existing animation
      this.stopAnimation();

      // Set CSS custom properties for animation
      this.marqueeContent.style.setProperty('--marquee-duration', `${duration}ms`);
      this.marqueeContent.style.setProperty('--marquee-distance', `-${contentWidth}px`);

      // Start animation
      this.startAnimation();
    }

    startAnimation() {
      if (this.isPaused) return;
      
      this.marqueeContent.style.animationPlayState = 'running';
      this.marqueeWrapper.classList.add('marquee-active');
    }

    stopAnimation() {
      this.marqueeContent.style.animationPlayState = 'paused';
    }

    handleMouseEnter() {
      this.isPaused = true;
      this.stopAnimation();
    }

    handleMouseLeave() {
      this.isPaused = false;
      this.startAnimation();
    }
  }

  customElements.define('sa-logo-carousel', SaLogoCarousel);
}
